/**
 * What a section is for — the page somebody lands on when they open an area
 * of the ERP.
 *
 * It answers the same question in both states, which is why it is one
 * component rather than two. When the app is installed it is an overview:
 * here is what this part of the system does, and here is the way in. When it
 * is not, the same words explain what the company would be turning on, what it
 * needs first, and offer to install it.
 *
 * Nothing here is a dead end. A section that cannot be entered still says why
 * and what to do about it, because "you clicked a menu item and got a blank
 * page" is the failure this screen exists to prevent.
 */
import { useTranslation } from "react-i18next";
import { AlertCircle, ArrowRight, Check, Lock } from "lucide-react";
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from "@erp/ui";

import { useSession } from "@/lib/session/use-session";
import { appPresentation, CORE, type AppCode } from "@/lib/modules";

import { APP_MANAGE_PERMISSION, type AppRecord } from "../api";
import { useApps, useInstallApp } from "../queries";
import { refusalMessage } from "../refusal";

export interface AppOverviewPageProps {
  code: AppCode;
  /** Where the section's real screens live, so the overview can lead into them. */
  entries?: readonly { path: string; labelKey: string }[];
  onNavigate?: (path: string) => void;
  onOpenApps?: () => void;
}

export function AppOverviewPage({
  code,
  entries = [],
  onNavigate,
  onOpenApps,
}: AppOverviewPageProps) {
  const { t } = useTranslation();
  const { hasModule, hasPermission } = useSession();
  const installed = hasModule(code);
  const presentation = appPresentation(code);

  // The catalogue is only needed to explain an app that is NOT installed —
  // its dependencies and their state. Asking for it on every section overview
  // would be a request nobody reads.
  const catalogue = useApps(!installed && hasPermission("platform.module.read"));
  const record = catalogue.data?.find((entry) => entry.code === code);

  const name = t(`apps.${code}.name`, code);
  const Icon = presentation?.icon;

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-3">
          {Icon === undefined ? null : (
            <span className="flex size-10 items-center justify-center rounded-lg bg-muted">
              <Icon className="size-5" aria-hidden="true" />
            </span>
          )}
          <h1 className="text-xl font-semibold tracking-tight">{name}</h1>
          {code === CORE ? (
            <Badge variant="secondary">{t("apps.alwaysOn", "Always on")}</Badge>
          ) : (
            <Badge variant={installed ? "success" : "outline"}>
              {installed
                ? t("apps.installed", "Installed")
                : t("apps.notInstalled", "Not installed")}
            </Badge>
          )}
        </div>
        <p className="max-w-2xl text-sm text-muted-foreground">{t(`apps.${code}.tagline`, "")}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>{t("apps.whatYouCanDo", "What you can do here")}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm">
            {(["b1", "b2", "b3", "b4"] as const).map((bullet) => {
              const text = t(`apps.${code}.${bullet}`, "");
              if (text === "") return null;
              return (
                <li key={bullet} className="flex items-start gap-2">
                  <Check
                    className="mt-0.5 size-4 shrink-0 text-muted-foreground"
                    aria-hidden="true"
                  />
                  <span>{text}</span>
                </li>
              );
            })}
          </ul>
        </CardContent>
      </Card>

      {installed ? (
        <InstalledSection entries={entries} onNavigate={onNavigate} />
      ) : (
        <NotInstalledSection
          code={code}
          name={name}
          record={record}
          canManage={hasPermission(APP_MANAGE_PERMISSION)}
          onOpenApps={onOpenApps}
        />
      )}
    </div>
  );
}

function InstalledSection({
  entries,
  onNavigate,
}: {
  entries: readonly { path: string; labelKey: string }[];
  onNavigate?: (path: string) => void;
}) {
  const { t } = useTranslation();
  if (entries.length === 0 || onNavigate === undefined) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <h2>{t("apps.open", "Open")}</h2>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-2">
          {entries.map((entry) => (
            <Button
              key={entry.path}
              type="button"
              variant="outline"
              onClick={() => onNavigate(entry.path)}
            >
              {t(entry.labelKey, entry.path)}
              <ArrowRight className="size-4 rtl:rotate-180" aria-hidden="true" />
            </Button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function NotInstalledSection({
  code,
  name,
  record,
  canManage,
  onOpenApps,
}: {
  code: AppCode;
  name: string;
  record: AppRecord | undefined;
  canManage: boolean;
  onOpenApps?: () => void;
}) {
  const { t } = useTranslation();
  const { hasModule } = useSession();
  const install = useInstallApp();

  // The dependencies that are themselves missing. Naming them is the whole
  // point: an administrator told "install Catalog and Accounting first" can
  // act, and one told "dependencies unmet" cannot.
  const missing = (record?.depends_on ?? []).filter((dependency) => !hasModule(dependency));
  const refusalText = refusalMessage(t, install.error);

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <h2>{t("apps.notInstalledTitle", "{{name}} is not installed", { name })}</h2>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          {t("apps.notInstalledBody", "This company has not turned this app on yet.")}
        </p>

        {(record?.depends_on ?? []).length === 0 ? null : (
          <p className="text-sm">
            <span className="text-muted-foreground">{t("apps.needs", "Needs")}: </span>
            {(record?.depends_on ?? []).map((dependency, index) => (
              <span key={dependency}>
                {index === 0 ? "" : "، "}
                <span className={hasModule(dependency) ? "" : "text-destructive"}>
                  {t(`apps.${dependency}.name`, dependency)}
                </span>
              </span>
            ))}
          </p>
        )}

        {refusalText === "" ? null : (
          <p role="alert" className="flex items-start gap-2 text-sm text-destructive">
            <AlertCircle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            <span>{refusalText}</span>
          </p>
        )}

        {canManage ? (
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              disabled={install.isPending || missing.length > 0}
              onClick={() =>
                install.mutate({ code, expectedVersion: record?.version || undefined })
              }
            >
              {install.isPending
                ? t("apps.installing", "Installing…")
                : t("apps.install", "Install")}
            </Button>
            {onOpenApps === undefined ? null : (
              <Button type="button" variant="outline" onClick={onOpenApps}>
                {t("apps.goToApps", "Manage apps")}
              </Button>
            )}
          </div>
        ) : (
          <p className="flex items-start gap-2 text-sm text-muted-foreground">
            <Lock className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            <span>
              {t("apps.manageForbidden", "You cannot change which apps this company has.")}
            </span>
          </p>
        )}
      </CardContent>
    </Card>
  );
}
